import { sha256 } from "js-sha256";
import { v5 as uuidv5 } from "uuid";

import { Uuid } from "@/domains/global/identifiers";
import { resolveSyncModelNamespacedUuid } from "@/modules/uuid/sync-models/resolveSyncModelNamespacedUuid";
import { SyncModelKind } from "@/store/sync/types";

/**
 * `NoteAttachment` is a BE-only row, not a sync model — only its derived
 * `SpaceAccountNoteAttachmentSyncModel` is synced. So we can't reuse
 * `resolveSyncModelNamespacedUuid`: BE's `resolve_note_attachment_uuid` uses
 * a bespoke namespace + algorithm (`uuid5(NS, "{noteId}::{sha256(url)}")`)
 * that predates the sync-model UUID convention. We mirror BE byte-for-byte.
 *
 * Source: `workspaces/python/services/sync-engine/packages/protocol/src/mem/sync_engine_protocol/ids.py`.
 *
 * The namespace's variant bits don't match RFC 4122. Python's `uuid.UUID`
 * accepts any 32-hex string; `uuid@9.parse()` rejects it. We bypass the
 * strict validator by passing pre-decoded bytes to `uuid.v5`.
 */
const NOTE_ATTACHMENT_NAMESPACE_BYTES = decodeUuid("A7B8C9D0-E1F2-3A4B-5C6D-7E8F9A0B1C2D");

export const resolveNoteAttachmentUuid = (noteId: Uuid, attachmentUrl: string): Uuid => {
  const name = `${noteId.toUpperCase()}::${sha256(attachmentUrl)}`;
  return uuidv5(name, NOTE_ATTACHMENT_NAMESPACE_BYTES);
};

export const resolveSpaceAccountNoteAttachmentSyncModelUuid = (noteAttachmentId: Uuid): Uuid =>
  resolveSyncModelNamespacedUuid({
    modelKind: { value: SyncModelKind.SpaceAccountNoteAttachment },
    uuids: [noteAttachmentId],
  });

function decodeUuid(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, "");
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}
